import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, ExternalLink, GraduationCap, FileText, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const GoogleScholarSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { toast } = useToast();

  const quickSearchTerms = [
    'vaquita porpoise conservation',
    'phocoena sinus endangered',
    'gulf of california marine mammals',
    'gillnet bycatch vaquita',
    'vaquita population decline'
  ];

  const handleSearch = async (term = searchTerm) => {
    if (!term.trim()) {
      toast({
        title: "Enter search term",
        description: "Please enter a search term to find research papers.",
        duration: 3000,
      });
      return;
    }

    setIsSearching(true);
    
    // Simulate search delay
    setTimeout(() => {
      const scholarUrl = `https://scholar.google.com/scholar?q=${encodeURIComponent(term)}`;
      window.open(scholarUrl, '_blank', 'noopener noreferrer');
      setIsSearching(false);
      
      toast({
        title: "Search opened in new tab",
        description: `Searching Google Scholar for: "${term}"`,
        duration: 4000,
      });
    }, 500);
  };

  const handleQuickSearch = (term) => {
    setSearchTerm(term);
    handleSearch(term);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <GraduationCap className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
          Google Scholar Research
        </h3>
        <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto">
          Search academic papers and scientific publications about vaquita conservation, 
          marine biology, and cetacean research.
        </p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Enter research terms (e.g., vaquita conservation, marine mammal bycatch...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-14 bg-gray-700 border-gray-600 text-white placeholder-gray-400 text-lg"
          />
        </div>
        
        <Button 
          type="submit"
          disabled={isSearching}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg"
        >
          {isSearching ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              Searching...
            </>
          ) : (
            <>
              <ExternalLink className="h-5 w-5 mr-3" />
              Search Google Scholar
            </>
          )}
        </Button>
      </form>

      {/* Quick Search Buttons */}
      <div className="mt-8">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-blue-400" />
          Popular Research Topics
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickSearchTerms.map((term, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => handleQuickSearch(term)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white text-left justify-start h-auto py-3 px-4 whitespace-normal"
            >
              <Search className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm leading-tight">{term}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Info Notice */}
      <div className="mt-8 bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-yellow-300 font-semibold mb-2">Research Tip</h5>
            <p className="text-yellow-200 text-sm leading-relaxed">
              Google Scholar searches will open in a new tab. For best results, use specific terms like 
              "vaquita," "Phocoena sinus," "Gulf of California," or "gillnet bycatch." 
              Combine terms for more targeted results.
            </p>
          </div>
        </div>
      </div>

      {/* Recent Publications Preview */}
      <div className="mt-8 pt-6 border-t border-gray-700">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-green-400" />
          Featured Research Areas
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-700/30 rounded-lg p-4">
            <h5 className="text-white font-medium mb-2">Population Studies</h5>
            <p className="text-gray-400 text-sm">
              Latest research on vaquita population estimates, acoustic monitoring, and habitat surveys.
            </p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4">
            <h5 className="text-white font-medium mb-2">Conservation Strategies</h5>
            <p className="text-gray-400 text-sm">
              Studies on fishing restrictions, alternative livelihoods, and conservation effectiveness.
            </p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4">
            <h5 className="text-white font-medium mb-2">Bycatch Research</h5>
            <p className="text-gray-400 text-sm">
              Analysis of gillnet impacts, fishing gear modifications, and mortality rates.
            </p>
          </div>
          <div className="bg-gray-700/30 rounded-lg p-4">
            <h5 className="text-white font-medium mb-2">Genetic Studies</h5>
            <p className="text-gray-400 text-sm">
              Genetic diversity analysis, population genetics, and evolutionary research.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleScholarSearch;